import { useCallback, useRef, useState, type ReactNode } from 'react';
import {
  captureSummaryV3,
  financeAct,
  financeDenialRework,
  BOARD_PACK_PATH,
  financeFacility,
  financeSar,
  financeSarAction,
  financeSarDismiss,
  financeTrustReconcile,
  financeTrustTask,
  type FacilityBoard,
  type FinanceFigure,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useShownHonesty, useTagSuffix } from '../../session/DemoScope';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { BlockHead, KpiStrip, Waiting, type Kpi } from './Kpis';
import { TONE, badgeOf, bs, btnS, downloadFrom, k1, pre, rowS, spark, useFinanceApi, whole } from './lib';
import { usePilot } from './pilot';
import { PilotPayBlock } from './PilotBlocks';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const usdOf = (f: FinanceFigure | null | undefined) => (f?.value == null ? null : f.unit === 'cents' ? f.value / 100 : f.value);
const kR = (usd: number) => (usd >= 1000 ? '$' + Math.round(usd / 1000) + 'K' : '$' + Math.round(usd));
const PAYER_C = ['#2F5C97', '#1F6B52', '#54606C', '#22272E'];

/** When a report is due, as the design says it: "review by 4pm", "2 days". */
function dueWords(iso: string | null, now: number): string {
  if (!iso) return 'no due date';
  const ms = Date.parse(iso) - now;
  if (ms < 0) return 'overdue';
  if (ms < 86_400_000) {
    const d = new Date(iso);
    const h = d.getHours();
    return 'review by ' + (h % 12 || 12) + (d.getMinutes() ? ':' + String(d.getMinutes()).padStart(2, '0') : '') + (h < 12 ? 'am' : 'pm');
  }
  const days = Math.ceil(ms / 86_400_000);
  return days + (days === 1 ? ' day' : ' days');
}

type Confirm = { title: string; body: ReactNode; label: string; run: () => Promise<void> } | null;

/**
 * Finance, Facility scope (design ff, T10390-10538): one building's money. Operator and
 * Super Admin pick any building; the Partner reads; a Facility Admin gets only their own,
 * with no switch. Peers are a percentile, never by name.
 */
export function FacilityTreasury({ facility, facName, canAct }: { facility: string | undefined; facName: string; canAct: boolean }) {
  const { client, session } = useSession();
  // The building's pilot (its own building for a Facility Admin).
  const { pilot } = usePilot(facility ?? session?.user.organizationId ?? null);
  // In a seeded building scope (no banner, no "demo" on screen): no SAMPLE tag on each figure.
  const tag = useShownHonesty();
  const suffix = useTagSuffix();
  const q = useFinanceApi((c, signal) => financeFacility(c, { query: { facility }, signal }), [facility]);
  const cap = useFinanceApi((c, signal) => captureSummaryV3(c, { query: { facility }, signal }), [facility]);
  const sar = useFinanceApi((c, signal) => financeSar(c, { query: { facility }, signal }), [facility]);
  const [done, setDone] = useState<Record<string, string>>({});
  const [all, setAll] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  // The building's ledger export (restored from production): the board pack at facility scope.
  const [ledger, setLedger] = useState<'idle' | 'busy' | 'failed'>('idle');
  const exportLedger = async () => {
    setLedger('busy');
    const ok = await downloadFrom(client, BOARD_PACK_PATH + '?format=csv' + (facility ? '&facility=' + encodeURIComponent(facility) : ''), 'ledger.csv').catch(() => false);
    setLedger(ok ? 'idle' : 'failed');
  };
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [reason, setReason] = useState('');
  // the dismiss reason, read when the dialog confirms
  const reasonRef = useRef('');
  // stable, so the shared dialog does not refocus Cancel on every keystroke in the reason box
  const closeConfirm = useCallback(() => setConfirm(null), []);
  const now = Date.now();

  const d = q.data;
  const b: FacilityBoard | undefined = d?.board;
  const run = (id: string, doneLabel: string, fn: () => Promise<unknown>) => async () => {
    setErr(null);
    try {
      await fn();
      setDone((x) => ({ ...x, [id]: doneLabel }));
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const stage = (key: string) => d?.claimsPipeline.stages.find((s) => s.key === key) ?? null;
  const paid = stage('paid');
  const submitted = stage('submitted');
  const denied = stage('denied');
  // claimsPipeline.real is false when the building has no eligible claim at all: that is
  // an empty ledger, not sample data, so the figures stay LIVE and "Collected" is absent
  // ("no claims billed yet"), never "$0 SAMPLE". The pipeline is every claim to date.
  const pipeFlags = d ? { basis: 'measured' } : null;
  const noClaims = !!d && !d.claimsPipeline.real;
  const capPeer = d?.sections.peers.capturePerResident.value ?? null;
  const denialPct = submitted && denied && submitted.amount ? (denied.amount / submitted.amount) * 100 : null;
  // Still capturable is Capture's Not billed for the same building (documented this month
  // minus what was billed), served once on the capture summary, so the two pages agree.
  const opp = cap.data?.notBilled ?? cap.data?.opportunity;
  const h = b?.history ?? {};
  const held = usdOf(d?.sections.trust.inCustody);
  const accounts = d?.sections.trust.accounts.value ?? null;

  const kpis: Kpi[] = d
    ? [
        {
          // COLLECTED: reimbursement paid on claims billed through Aithora, every claim to
          // date. Not Capture's "documented and billable this month" (the charge-capture
          // engine, which Performance also shows): the two are different money.
          k: 'Collected',
          v: paid && !noClaims ? k1(paid.amount) : '–',
          badge: badgeOf(pipeFlags),
          note: (noClaims ? 'no claims billed yet' : 'all claims to date') + (capPeer != null ? ' · p' + capPeer + ' of peers' : ''),
          pts: spark(h.captured ?? []),
          c: '#1F6B52',
          tip: 'Collected reimbursement. Never added to the capturable figure',
        },
        {
          k: 'Still capturable',
          v: opp?.value != null ? k1(opp.value / 100) : '–',
          badge: badgeOf(opp),
          note: 'documented, not yet billed',
          pts: spark(h.capturable ?? []),
          c: '#264C7E',
          tip: 'Capture’s Not billed for this building: care documented this month minus what the practitioner billed',
        },
        {
          k: 'Days to collect',
          v: b?.daysToCollect.value != null ? Math.round(b.daysToCollect.value) + ' days' : '–',
          badge: badgeOf(b?.daysToCollect),
          note: 'healthy is 30–40 (HFMA)' + (b?.peers.daysToCollect != null ? ' · p' + b.peers.daysToCollect : ''),
          pts: spark(h.daysToCollect ?? []),
          c: '#22272E',
          tip: 'Days in A/R from live claim aging',
        },
        {
          k: 'Denial rate',
          v: denialPct != null ? denialPct.toFixed(1) + '%' : '–',
          badge: badgeOf(pipeFlags),
          note: 'of submitted' + (b?.peers.denialRate != null ? ' · p' + b.peers.denialRate : ''),
          pts: spark(h.denialRate ?? []),
          c: '#B3261E',
          tip: 'Denied dollars of submitted dollars, every claim to date',
        },
        {
          k: 'Money protected',
          v: usdOf(b?.moneyProtected) != null ? k1(usdOf(b?.moneyProtected)!) : '–',
          badge: badgeOf(b?.moneyProtected),
          note: 'scams stopped this quarter',
          pts: spark(h.protected ?? []),
          c: '#22272E',
          tip: 'Guardian blocks × typical loss; not a measured recovery',
        },
        {
          k: 'Resident funds',
          v: held != null ? k1(held) : '–',
          badge: badgeOf(d.sections.trust.inCustody),
          note: (accounts ?? 0) + ' accounts held in trust',
          pts: spark(h.funds ?? []),
          c: '#22272E',
          tip: 'Sum of resident trust balances',
        },
      ]
    : [];

  const acts = b?.actions ?? [];
  const PIPE: [string, string][] = [
    ['submitted', 'Submitted'],
    ['accepted', 'Accepted'],
    ['paid', 'Collected'],
    ['denied', 'Denied'],
  ];
  const bond = usdOf(d?.sections.trust.suretyBond);
  const checks = [
    ...(bond != null && held != null
      ? [{ id: 'bond', k: 'Surety bond covers all funds', note: 'Bond ' + kR(bond) + ' vs ' + k1(held) + ' held · ' + Math.round((held / bond) * 100) + '% used', ok: bond >= held, act: null as string | null, doneLabel: '' }]
      : []),
    ...(b?.trustChecks ?? []).map((c) => ({ id: c.id, k: c.rule, note: c.note, ok: c.ok, act: c.action, doneLabel: c.doneLabel })),
  ];
  const near = (d?.nearMedicaidCap ?? []).slice().sort((x, y) => y.balanceCents - x.balanceCents);
  const nearShow = all.near ? near : near.slice(0, 2);
  const recon = b?.reconciliations ?? [];
  const reconShow = all.recon ? recon : recon.slice(-6);
  const last = recon[recon.length - 1];
  const reconOpen = !!last && !last.balanced && !done.recon;
  const charges = b?.charges ?? [];
  const chargesShow = all.charges ? charges : charges.slice(0, 2);
  const cases = (sar.data?.reports ?? []).filter((r) => r.status === 'open');
  const sarWord = sar.data ? badgeOf({ basis: 'measured' }) : 'NOT WIRED';
  const filed = (sar.data?.reports ?? []).filter((r) => r.status === 'filed').length;
  const plan = b?.plan;

  const toggle = (k: string) => (e: React.MouseEvent) => {
    pre(e);
    setAll((a) => ({ ...a, [k]: !a[k] }));
  };
  const subhead = (label: string, tip: string, word: string, k: string, n: number) => (
    <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:nowrap;margin-top:8px;min-width:0;')}>
      <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;white-space:nowrap;')}>{label}</span>
      <span title={tip} style={sx('font-size:14px;color:#54606C;white-space:nowrap;flex:0 0 auto;')}>
        <span>{tag(word) ?? ''}</span>
      </span>
      <span style={sx('flex:1 1 10px;')} />
      {n > 0 && (
        <a href="#" onClick={toggle(k)} style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
          <span>{all[k] ? 'Show less' : 'View all ' + n}</span>
        </a>
      )}
    </span>
  );
  const word = d ? badgeOf({ basis: 'measured' }, d.real) : 'NOT WIRED';
  const linkS = 'display:inline-flex;align-items:center;min-height:32px;font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;';

  if (q.error && !d) {
    return (
      <div role="alert" style={sx('font-size:13px;color:#B3261E;padding:14px 0;')}>
        {q.error.message}
      </div>
    );
  }

  return (
    <div data-screen-label="Finance · Facility" data-testid="finance-facility" style={sx('height:100%;flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:10px;min-width:0;')} aria-busy={q.loading && !d ? 'true' : undefined}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('font-size:19px;font-weight:800;letter-spacing:-.02em;color:#22272E;white-space:nowrap;')}>
          <span>{facName + ' money'}</span>
        </span>
        <span style={sx('font-size:14px;color:#54606C;')}>reimbursement, claims and resident funds · peers shown as a percentile, never by name</span>
        <span style={sx('font-size:13px;font-weight:700;letter-spacing:.06em;color:#54606C;white-space:nowrap;')}>
          <span>{d ? (d.real ? (tag('LIVE') ? 'LIVE figures' : '') : tag('SAMPLE') ? 'SAMPLE figures' : '') : ''}</span>
        </span>
        <span style={sx('flex:1 1 10px;')} />
        <a
          href="#"
          title="Download this building's figures as CSV, each stamped with its basis"
          onClick={(e) => {
            pre(e);
            if (ledger !== 'busy') void exportLedger();
          }}
          style={sx('color:#2F5C97;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;')}
        >
          <span>{ledger === 'busy' ? 'Preparing' : ledger === 'failed' ? 'Export failed' : 'Export ledger'}</span>
        </a>
      </div>
      <KpiStrip kpis={kpis} testId="finance-facility-kpis" />
      {err && (
        <div role="alert" style={sx('font-size:14px;color:#B3261E;')}>
          {err}
        </div>
      )}
      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr));gap:12px 24px;align-content:start;min-width:0;padding-right:3px;')}>
        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;')} data-testid="finance-acts">
          <BlockHead title="Act now" meta={acts.filter((a) => !done[a.id]).length + ' open · signal → why → owner → action'} />
          {acts.map((a, i) => {
            const badge = badgeOf(a);
            return (
              <div key={a.id} style={sx(rowS(i, !!done[a.id]))}>
                <span style={sx('display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 220px;')}>
                  <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;min-width:0;')}>
                    <span style={sx('font-size:13.5px;font-weight:800;color:#22272E;')}>
                      <span>{a.signal}</span>
                    </span>
                    <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:' + TONE[a.tone] + ';')}>
                      <span>{a.money}</span>
                    </span>
                    {tag(badge) && (
                      <span style={sx(bs(badge))}>
                        <span>{badge}</span>
                      </span>
                    )}
                  </span>
                  <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>
                    <span>{a.why}</span> <span style={sx('color:#54606C;white-space:nowrap;')}>· <span>{a.owner}</span></span>
                  </span>
                </span>
                <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;flex:0 0 auto;')}>
                  {canAct &&
                    !done[a.id] &&
                    a.actions.map((x) => (
                      <a
                        key={x.label}
                        href="#"
                        onClick={(e) => {
                          pre(e);
                          void run(a.id, a.doneLabel, () => financeAct(client, { params: { id: a.id }, body: { action: x.label, facility } }))();
                        }}
                        style={sx(btnS(x.primary))}
                      >
                        <span>{x.label}</span>
                      </a>
                    ))}
                  {!!done[a.id] && (
                    <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>
                      {'✓ '}
                      <span>{done[a.id]}</span>
                    </span>
                  )}
                  {!canAct && !done[a.id] && <span style={sx('font-size:14px;color:#54606C;')}>View only</span>}
                </span>
              </div>
            );
          })}
          {d && !b && <Waiting what="the Act now signals for this building." />}
        </div>

        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-pipeline">
          <BlockHead title="Claims pipeline" meta={'all claims to date' + suffix(d ? badgeOf(pipeFlags) : '')} />
          {d &&
            PIPE.map(([key, label], i) => {
              const s = stage(key);
              const base = submitted?.amount || 1;
              return (
                <span key={key} style={sx('display:grid;grid-template-columns:84px minmax(0,1fr) 92px;gap:8px;align-items:center;min-height:24px;')}>
                  <span style={sx('font-size:14px;color:#3C454F;white-space:nowrap;')}>
                    <span>{label}</span>
                  </span>
                  <span style={sx('position:relative;height:12px;background:rgba(34,39,46,.06);border-radius:2px;')}>
                    <span style={sx('position:absolute;left:0;top:0;bottom:0;border-radius:2px;width:' + (((s?.amount ?? 0) / base) * 100).toFixed(1) + '%;background:' + (i === 3 ? '#B3261E' : i === 2 ? '#1F6B52' : '#2F5C97') + ';')} />
                  </span>
                  <span style={sx('font-size:14px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:#22272E;white-space:nowrap;')}>
                    <span>{s ? k1(s.amount) + ' · ' + s.count : '–'}</span>
                  </span>
                </span>
              );
            })}
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;margin-top:6px;')}>TOP DENIAL REASONS</span>
          {(b?.denialReasons ?? []).map((x) => (
            <span key={x.id} style={sx('display:flex;align-items:center;gap:6px 10px;flex-wrap:wrap;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
              <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 140px;')}>
                <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                  <span>{x.reason}</span>
                </span>
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{x.claims + (x.claims === 1 ? ' claim' : ' claims') + (x.code ? ' · ' + x.code : '')}</span>
                </span>
              </span>
              <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:#B3261E;white-space:nowrap;')}>
                <span>{'$' + x.usdK.toFixed(1) + 'K'}</span>
              </span>
              {canAct && !done['dn-' + x.id] && (
                <a
                  href="#"
                  onClick={(e) => {
                    pre(e);
                    void run('dn-' + x.id, 'Queued', () => financeDenialRework(client, { params: { id: x.id }, body: { facility } }))();
                  }}
                  style={sx(linkS)}
                >
                  Rework
                </a>
              )}
              {!!done['dn-' + x.id] && <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>{'✓ Queued'}</span>}
            </span>
          ))}
          {d && !b && <Waiting what="denial reasons grouped by cause." />}
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;margin-top:6px;')}>
            <span>{'PAYER MIX' + suffix(b ? badgeOf({ basis: b.payerMix.basis }) : 'NOT WIRED')}</span>
          </span>
          <div style={sx('display:flex;height:10px;border-radius:3px;overflow:hidden;gap:2px;')}>
            {(b?.payerMix.rows ?? []).map((g, i) => (
              <span key={g.payer} title={g.payer + ' ' + g.pct + '%'} style={sx('flex:' + g.pct + ' 1 0;background:' + PAYER_C[i % 4] + ';')} />
            ))}
          </div>
          <div style={sx('display:flex;gap:4px 12px;flex-wrap:wrap;')}>
            {(b?.payerMix.rows ?? []).map((g, i) => (
              <span key={g.payer} style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#3C454F;white-space:nowrap;')}>
                <span style={sx('width:8px;height:8px;border-radius:2px;background:' + PAYER_C[i % 4] + ';')} />
                <span>{g.payer}</span> <b>{g.pct + '%'}</b>
              </span>
            ))}
          </div>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;margin-top:6px;')}>
            <span>{'DAYS TO PAYMENT BY PAYER' + suffix(b ? badgeOf({ basis: b.payerMix.basis }) : 'NOT WIRED')}</span>
          </span>
          {(b?.daysToPayment ?? []).map((x) => (
            <span key={x.payer} style={sx('display:grid;grid-template-columns:96px minmax(0,1fr) 64px;gap:8px;align-items:center;min-height:20px;')}>
              <span style={sx('font-size:14px;color:#3C454F;white-space:nowrap;')}>
                <span>{x.payer}</span>
              </span>
              <span style={sx('position:relative;height:8px;background:rgba(34,39,46,.06);border-radius:2px;')}>
                <span style={sx('position:absolute;left:0;top:0;bottom:0;width:' + Math.min(100, (x.days / 45) * 100) + '%;border-radius:2px;background:' + (x.days > x.target ? '#B3261E' : '#2F5C97') + ';')} />
              </span>
              <span style={sx('font-size:14px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:' + (x.days > x.target ? '#B3261E' : '#22272E') + ';white-space:nowrap;')}>
                <span>{x.days + ' days'}</span>
              </span>
            </span>
          ))}
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;margin-top:6px;')}>
            <span>{'COLLECTED OF BILLED · 6 MONTHS' + suffix(b ? badgeOf({ basis: b.payerMix.basis }) : 'NOT WIRED')}</span>
          </span>
          <div style={sx('display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:6px;align-items:end;min-width:0;')}>
            {(b?.collectedOfBilled ?? []).map((m) => (
              <span key={m.month} style={sx('display:flex;flex-direction:column;align-items:center;gap:3px;min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (m.pct >= 95 ? '#1F6B52' : '#22272E') + ';')}>
                  <span>{m.pct + '%'}</span>
                </span>
                <span style={sx('width:100%;height:34px;display:flex;align-items:flex-end;background:rgba(34,39,46,.05);border-radius:2px;')}>
                  <span style={sx('display:block;width:100%;height:' + Math.max(0, ((m.pct - 80) / 20) * 100) + '%;border-radius:2px;background:' + (m.pct >= 95 ? '#1F6B52' : '#2F5C97') + ';')} />
                </span>
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{m.month}</span>
                </span>
              </span>
            ))}
          </div>
        </div>

        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-trust">
          <BlockHead title="Resident funds · trust rules" meta="42 CFR 483.10(f)(10)" />
          <span style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;')}>
            <span style={sx('font-size:17px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;')}>
              <span>{held != null ? k1(held) : '–'}</span>
            </span>
            <span style={sx('font-size:14px;color:#54606C;')}>
              <span>
                {(accounts ?? 0) +
                  ' accounts · ' +
                  (d?.sections.trust.reconciliationAgeDays.value != null ? 'reconciled ' + d.sections.trust.reconciliationAgeDays.value + ' days ago' : 'never reconciled')}
              </span>
            </span>
          </span>
          {checks.map((c) => {
            const ok = c.ok || !!done['chk-' + c.id];
            return (
              <span key={c.id} style={sx('display:flex;align-items:center;gap:6px 10px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
                <span style={sx('width:9px;height:9px;border-radius:50%;flex:0 0 9px;background:' + (ok ? '#1F6B52' : '#B3261E') + ';')} />
                <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 160px;')}>
                  <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                    <span>{c.k}</span>
                  </span>
                  <span style={sx('font-size:14px;color:' + (ok ? '#54606C' : '#B3261E') + ';')}>
                    <span>{done['chk-' + c.id] ? 'Fixed today' : c.note}</span>
                  </span>
                </span>
                {canAct && !ok && c.act && (
                  <a
                    href="#"
                    onClick={(e) => {
                      pre(e);
                      void run('chk-' + c.id, c.doneLabel, () => financeTrustTask(client, { body: { kind: 'rule', id: c.id, facility } }))();
                    }}
                    style={sx(linkS)}
                  >
                    <span>{c.act}</span>
                  </a>
                )}
                {!!done['chk-' + c.id] && (
                  <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>
                    {'✓ '}
                    <span>{done['chk-' + c.id]}</span>
                  </span>
                )}
              </span>
            );
          })}
          {subhead('NEAR THE MEDICAID LIMIT', 'Medicaid asset limit', word, 'near', near.length)}
          {nearShow.map((x) => {
            const red = x.balanceCents >= x.capCents * 0.95;
            const id = 'near-' + x.residentId;
            return (
              <span key={x.residentId} style={sx('display:grid;grid-template-columns:minmax(0,110px) minmax(0,1fr) auto auto;gap:8px;align-items:center;min-height:30px;')}>
                <span style={sx('font-size:14px;font-weight:700;color:#22272E;overflow-wrap:break-word;')}>
                  <span>{x.resident}</span>
                </span>
                <span style={sx('position:relative;height:8px;background:rgba(34,39,46,.06);border-radius:2px;')}>
                  <span style={sx('position:absolute;left:0;top:0;bottom:0;width:' + Math.min(100, (x.balanceCents / x.capCents) * 100) + '%;border-radius:2px;background:' + (red ? '#B3261E' : '#2F5C97') + ';')} />
                </span>
                <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (red ? '#B3261E' : '#22272E') + ';white-space:nowrap;')}>
                  <span>{whole(x.balanceCents)}</span>
                </span>
                <span style={sx('display:inline-flex;')}>
                  {canAct && !done[id] && (
                    <a
                      href="#"
                      onClick={(e) => {
                        pre(e);
                        void run(id, 'Planned', () => financeTrustTask(client, { body: { kind: 'spend_down', id: x.residentId, facility } }))();
                      }}
                      style={sx(linkS)}
                    >
                      Plan spend-down
                    </a>
                  )}
                  {!!done[id] && (
                    <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>
                      {'✓ '}
                      <span>{done[id]}</span>
                    </span>
                  )}
                </span>
              </span>
            );
          })}
          {subhead('MONTHLY RECONCILIATION', '3-way reconciliation: ledger, bank, statements', b ? badgeOf({ basis: 'measured' }, d?.real) : 'NOT WIRED', 'recon', recon.length)}
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(42px,1fr));gap:6px;min-width:0;')}>
            {reconShow.map((m, i) => {
              const ok = m.balanced || (m === last && !!done.recon);
              return (
                <span
                  key={m.month + i}
                  title={ok ? 'Ledgers, control account and bank agree' : 'Ledgers ' + whole(m.ledgerCents) + ' vs bank ' + whole(m.bankCents) + ' · $' + Math.abs(Math.round((m.ledgerCents - m.bankCents) / 100)) + ' apart'}
                  style={sx('display:flex;align-items:center;justify-content:center;gap:4px;padding:3px 0;border-radius:6px;background:' + (ok ? 'rgba(31,107,82,.08)' : 'rgba(179,38,30,.10)') + ';')}
                >
                  <span style={sx('font-size:13px;font-weight:800;color:' + (ok ? '#1F6B52' : '#B3261E') + ';')}>{ok ? '✓' : '✗'}</span>
                  <span style={sx('font-size:14px;color:#3C454F;')}>
                    <span>{SHORT[Number(m.month.slice(5, 7)) - 1] ?? m.month}</span>
                  </span>
                </span>
              );
            })}
          </div>
          {reconOpen && (
            <span style={sx('display:flex;align-items:center;gap:6px 10px;flex-wrap:wrap;padding:4px 0;')}>
              <span style={sx('font-size:14px;color:#B3261E;flex:1 1 160px;')}>
                <span>{MONTHS[Number(last.month.slice(5, 7)) - 1] + ': ledgers and bank are $' + Math.abs(Math.round((last.ledgerCents - last.bankCents) / 100)) + ' apart'}</span>
              </span>
              {canAct && (
                <a
                  href="#"
                  onClick={(e) => {
                    pre(e);
                    const [y, mo] = last.month.split('-').map(Number);
                    const through = new Date(Date.UTC(y, mo, 0)).toISOString().slice(0, 10);
                    setConfirm({
                      title: 'Reconcile ' + MONTHS[mo - 1] + '?',
                      body: 'Records the reconciliation through ' + through + ' against the bank statement balance of ' + whole(last.bankCents) + '. The ledger side is computed by the server; any variance is kept on the record.',
                      label: 'Reconcile',
                      run: async () => {
                        await financeTrustReconcile(client, { body: withConfirm({ reconciledThrough: through, statementBalanceCents: last.bankCents }) });
                        setDone((x) => ({ ...x, recon: 'Reconciled' }));
                      },
                    });
                  }}
                  style={sx(linkS)}
                >
                  Reconcile
                </a>
              )}
            </span>
          )}
          {subhead('CHARGES TO REVIEW', 'Charges against resident personal funds', b ? badgeOf({ basis: 'measured' }, d?.real) : 'NOT WIRED', 'charges', charges.length)}
          {chargesShow.map((x) => {
            const id = 'ch-' + x.id;
            return (
              <span key={x.id} style={sx('display:flex;align-items:center;gap:4px 10px;flex-wrap:wrap;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
                <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 150px;')}>
                  <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                    <span>{x.resident}</span> · <span>{x.item}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#B3261E;')}>
                    <span>{x.why}</span>
                  </span>
                </span>
                <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;white-space:nowrap;')}>
                  <span>{whole(x.amountCents)}</span>
                </span>
                {canAct && !done[id] && (
                  <a
                    href="#"
                    onClick={(e) => {
                      pre(e);
                      void run(id, x.doneLabel, () => financeTrustTask(client, { body: { kind: 'charge', id: x.id, facility } }))();
                    }}
                    style={sx(linkS)}
                  >
                    <span>{x.action}</span>
                  </a>
                )}
                {!!done[id] && (
                  <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>
                    {'✓ '}
                    <span>{done[id]}</span>
                  </span>
                )}
              </span>
            );
          })}
        </div>

        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:14px;')}>
          <div style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;')} data-testid="finance-cases">
            <BlockHead title="Exploitation cases" meta={cases.filter((c) => !done['sar-' + c.id]).length + ' open · never read-only'} />
            {cases.map((c, i) => {
              const id = 'sar-' + c.id;
              const high = (c.severity ?? '').toLowerCase() === 'high';
              const BTNS: [string, boolean, string, () => Promise<unknown> | void][] = [
                ['Review', true, 'Reviewed', () => financeSarAction(client, { params: { id: c.id }, body: { action: 'review' } })],
                [
                  'Clear',
                  false,
                  'Cleared',
                  () => {
                    setReason('');
                    reasonRef.current = '';
                    setConfirm({
                      title: 'Clear this case?',
                      body: 'Dismisses the report. A reason is required and is kept on the record.',
                      label: 'Clear it',
                      run: async () => {
                        await financeSarDismiss(client, { params: { id: c.id }, body: { reason: reasonRef.current } });
                        setDone((x) => ({ ...x, [id]: 'Cleared' }));
                      },
                    });
                  },
                ],
                ['Escalate', false, 'Escalated', () => financeSarAction(client, { params: { id: c.id }, body: { action: 'escalate' } })],
                ['File APS', false, 'APS report filed', () => financeSarAction(client, { params: { id: c.id }, body: { action: 'file_aps' } })],
              ];
              return (
                <div key={c.id} style={sx(rowS(i, !!done[id]))}>
                  <span style={sx('display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 220px;')}>
                    <span style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;min-width:0;')}>
                      <span style={sx('font-size:13.5px;font-weight:800;color:#22272E;')}>
                        <span>{(c.resident ?? 'Resident') + ' · ' + (c.typology ?? 'suspicious activity')}</span>
                      </span>
                      <span style={sx('font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (high ? '#B3261E' : '#3C454F') + ';')}>
                        <span>{(c.severity ?? 'open').toUpperCase() + ' · ' + dueWords(c.dueAt, now)}</span>
                      </span>
                      <span style={sx(bs(sarWord))}>
                        <span>{sarWord}</span>
                      </span>
                    </span>
                    <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>
                      <span>{b?.sarSummaries[c.id] ?? ''}</span> <span style={sx('color:#54606C;white-space:nowrap;')}>· <span>Owner · Facility admin</span></span>
                    </span>
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;flex:0 0 auto;')}>
                    {canAct &&
                      !done[id] &&
                      BTNS.map(([label, primary, doneLabel, fn]) => (
                        <a
                          key={label}
                          href="#"
                          onClick={(e) => {
                            pre(e);
                            if (label === 'Clear') fn();
                            else void run(id, doneLabel, async () => fn())();
                          }}
                          style={sx(btnS(primary))}
                        >
                          <span>{label}</span>
                        </a>
                      ))}
                    {!!done[id] && (
                      <span style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>
                        {'✓ '}
                        <span>{done[id]}</span>
                      </span>
                    )}
                    {!canAct && !done[id] && <span style={sx('font-size:14px;color:#54606C;')}>View only</span>}
                  </span>
                </div>
              );
            })}
            <span style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-top:4px;')}>
              <span>
                {sar.data
                  ? 'SAR filing: ' + (filed ? filed + ' filed' : 'none filed yet') + '. Each open report is due ' + sar.data.dueRule.days + ' days after detection; a filed one leaves this list.'
                  : 'SAR filing: not tracked yet. Filing status appears here once the backend records it.'}
              </span>
            </span>
          </div>
          {pilot?.inPilot ? (
            // V9: during the free pilot the building pays $0, sees what Aithora made it, and no past bills.
            <PilotPayBlock pilot={pilot} />
          ) : (
          <div style={sx('min-width:0;display:flex;flex-direction:column;gap:6px;')} data-testid="finance-plan">
            <BlockHead title="What you pay Aithora" meta={plan ? plan.name + suffix(badgeOf({ basis: plan.basis })) : '–'} />
            {plan ? (
              <>
                <span style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:17px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;')}>
                    <span>{whole(plan.nextInvoiceCents)}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    next invoice · <span>{plan.nextDate}</span>
                  </span>
                </span>
                <span style={sx('font-size:14px;color:#3C454F;')}>
                  {/* onPlan is occupied beds (30-day average daily census), limit the licensed beds: not residents. */}
                  <span>{plan.onPlan + (plan.onPlanBasis === 'in_house' ? ' in the building' : plan.onPlanBasis === 'average_30d' ? ' occupied (30-day average)' : ' occupied') + ' of ' + plan.limit + ' licensed beds · ' + Math.round((plan.onPlan / Math.max(1, plan.limit)) * 100) + '% of limit'}</span>
                </span>
                <div style={sx('position:relative;height:10px;background:rgba(34,39,46,.08);border-radius:3px;')}>
                  <span style={sx('position:absolute;left:0;top:0;bottom:0;border-radius:3px;background:#2F5C97;width:' + Math.round((plan.onPlan / Math.max(1, plan.limit)) * 100) + '%;')} />
                </div>
                {plan.invoices.map((v) => (
                  <span key={v.month} style={sx('display:flex;align-items:center;gap:10px;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);font-size:14px;font-variant-numeric:tabular-nums;')}>
                    <span style={sx('flex:1 1 auto;color:#22272E;font-weight:700;')}>
                      <span>{v.month}</span>
                    </span>
                    <span style={sx('color:#22272E;font-weight:800;')}>
                      <span>{whole(v.amountCents)}</span>
                    </span>
                    <span style={sx('color:' + (v.status === 'Paid' ? '#1F6B52' : '#B3261E') + ';font-weight:700;white-space:nowrap;')}>
                      <span>{v.status}</span>
                    </span>
                  </span>
                ))}
              </>
            ) : (
              d && <Waiting what="this building's Aithora plan and invoices." />
            )}
          </div>
          )}
        </div>
      </div>
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          body={
            confirm.label === 'Clear it' ? (
              <>
                <p>{confirm.body}</p>
                <label style={sx('display:flex;flex-direction:column;gap:4px;font-size:14px;')}>
                  Reason
                  <textarea
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      reasonRef.current = e.target.value;
                    }}
                    rows={3}
                  />
                </label>
              </>
            ) : (
              confirm.body
            )
          }
          confirmLabel={confirm.label}
          onConfirm={async () => {
            if (confirm.label === 'Clear it' && !reasonRef.current.trim()) throw Object.assign(new Error('A reason is required.'), { kind: 'validation' });
            await confirm.run();
          }}
          onClose={closeConfirm}
        />
      )}
    </div>
  );
}

